import type {
  CatalogListItem,
  CatalogV2ListResponse,
} from '@skupreme/api-contracts/core';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useActiveScope } from '@features/account-view/active';
import { useDebouncedMemo } from '@hooks/debounceAsync';
import CatalogService, { isCatalogV2 } from '@utility/services/catalog';
import {
  buildCatalogListParams,
  buildCatalogV2ListParams,
  type CatalogListFilter,
  type CatalogListParams,
  type CatalogV2Filter,
  defaultCatalogFilter,
  defaultCatalogV2Filter,
} from '../lib/list-params';
import { catalogKeys } from '../lib/query-keys';
import { usePageVersions } from '@features/page-versions';
import { type CatalogSource, useCatalogSource } from './use-catalog-source';
import { useMarketSelection } from './use-market-selection';

const noItems: CatalogListItem[] = [];
const noNotes: string[] = [];
const noRows: CatalogV2ListResponse['data'] = [];

/*
 * The catalog list and its page counts. Same surface as the legacy
 * `catalogHook`, so the screen swaps one for the other unchanged.
 *
 * The API picks the shape by the account's catalog_page_v2 switch. The first
 * ask of a session carries the legacy params (a V2 API ignores them); once a
 * `source: 'v2'` answer is seen, the list asks with the V2 ones. The source
 * seen is kept in the query cache (cleared on logout), so later visits ask
 * with the right params at once. V2 has no component state, so a list
 * narrowed by `state` (the components page) stays legacy: it always asks with
 * the legacy params (the API sends `state=Component` to the legacy route) and
 * never writes the source the other lists read.
 */
export function useCatalogList(
  regionId: unknown,
  regionLoading = false,
  state?: string,
) {
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<CatalogListFilter>(defaultCatalogFilter);
  const [v2Filter, setV2Filter] = useState<CatalogV2Filter>(
    defaultCatalogV2Filter,
  );
  /* EN-4544: before this session's first answer, the account's page switch says which view to ask for and show, so a
     V2 account never flashes the legacy page first. */
  const versions = usePageVersions();
  const seenSource = useCatalogSource();
  const source: CatalogSource | null | undefined =
    seenSource ?? (versions.isV2('catalog') ? 'v2' : null);
  /* EN-4396: the keys below read the active level; a change of it renders this again. */
  const { level } = useActiveScope();

  /* M2: a new choice of countries starts again from the first page. */
  const { countries, onlyListed } = useMarketSelection();
  const marketsKey = `${countries.join()}:${onlyListed}`;
  const [seenMarkets, setSeenMarkets] = useState(marketsKey);
  if (seenMarkets !== marketsKey) {
    setSeenMarkets(marketsKey);
    setFilter(previous => ({ ...previous, page: 1 }));
  }

  const legacyOnly = !!state;
  const params =
    source === 'v2' && !legacyOnly
      ? buildCatalogV2ListParams(filter, v2Filter, { countries, onlyListed })
      : buildCatalogListParams(filter, regionId, state);
  /* Typing in the search box asks once it settles, not on every key. */
  const applied = useDebouncedMemo<CatalogListParams>(
    () => params,
    [JSON.stringify(params)],
    500,
  );
  const enabled = !!applied && !regionLoading;

  const listQuery = useQuery({
    queryKey: catalogKeys.list(applied ?? {}),
    queryFn: () => CatalogService.mergedData(applied),
    enabled,
  });

  const pageQuery = useQuery({
    queryKey: catalogKeys.pageData(applied ?? {}),
    queryFn: async () => (await CatalogService.pageData(applied)).data,
    enabled,
  });

  const response = listQuery.data;
  const v2Response = isCatalogV2<CatalogV2ListResponse>(response)
    ? response
    : undefined;
  const legacy =
    response && !isCatalogV2<CatalogV2ListResponse>(response)
      ? response
      : undefined;
  const answeredV2 = response ? !!v2Response : undefined;
  const failed = listQuery.isError;
  useEffect(() => {
    if (legacyOnly) return;
    const seen: CatalogSource | undefined =
      answeredV2 === undefined ? undefined : answeredV2 ? 'v2' : 'legacy';
    if (!seen && !failed) return;
    queryClient.setQueryData<CatalogSource | null>(
      catalogKeys.source(level),
      previous => seen ?? previous ?? 'legacy',
    );
  }, [answeredV2, failed, legacyOnly, level, queryClient]);

  const filterChanged = (key: keyof CatalogListFilter, value: unknown) => {
    setFilter(previous => ({ ...previous, page: 1, [key]: value }));
  };

  const v2FilterChanged = (key: keyof CatalogV2Filter, value: string) => {
    setFilter(previous => ({ ...previous, page: 1 }));
    setV2Filter(previous => ({ ...previous, [key]: value }));
  };

  const pageChanged = (page: number) => {
    setFilter(previous => ({ ...previous, page }));
  };

  /* Corrections the screen makes (a marketplace toggled) go into the cache. */
  const setData = (data: CatalogListItem[]) => {
    queryClient.setQueryData(catalogKeys.list(applied ?? {}), previous =>
      previous && !isCatalogV2(previous) ? { ...previous, data } : previous,
    );
  };

  return {
    data: legacy?.data ?? noItems,
    /** The V2 rows; empty on a legacy answer. */
    v2Data: v2Response?.data ?? noRows,
    /** EN-4134: what the V2 answer could not honour (a sort it fell back from, say); empty on a legacy one. */
    v2NotSupported: v2Response?.not_supported ?? noNotes,
    /** The API answered with `source: 'v2'` (and keeps that while the next page loads). */
    isV2: answeredV2 ?? (!legacyOnly && source === 'v2'),
    /** An answer has come this session, or failed: the view is known and stays so while later pages load. */
    known: answeredV2 !== undefined || (!legacyOnly && !!source),
    loading: !enabled || listQuery.isPending,
    error: listQuery.error?.message ?? pageQuery.error?.message ?? '',
    discontinuedItems: legacy?.discontinued_items ?? 0,
    totalPages: pageQuery.data?.total_pages ?? 0,
    totalData: pageQuery.data?.total_data ?? 0,
    filter,
    filterChanged,
    v2Filter,
    v2FilterChanged,
    pageChanged,
    setData,
    search: filter.search,
    /** The search the list was last asked with, for what loads beside it. */
    appliedSearch: String(applied?.search ?? ''),
    setSearch: (search: string) => filterChanged('search', search),
  };
}
